/**
 * Customers — the Hotel Supplier Management entry point.
 *
 * The workflow starts here: find a customer, then work outward to the hotels they
 * book, the agreements in flight, and the rate access codes those agreements
 * load against. This is a supplier-management VIEW of the customer base, not a
 * second customer master — identity still comes from the CRM (Domo Customer
 * Master), and clicking through goes to the canonical customer profile.
 *
 * Every internal user sees every customer (issue #1694). Enrollment in Supplier
 * Management is shown as a column because it is useful program data, but it is
 * NOT a filter on what is listed.
 */
import { Link } from 'react-router';
import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import {
  DetailRow,
  DisclosureCell,
  DisclosureHeaderCell,
  TableScroll,
  detailRowId,
  totalColumns,
  useExpandedRows,
  zebraFor,
} from '@/features/crm/components/ExpandableRow';
import { CustomerDetail, type Customer } from './CustomerDetail';
import type { AgreementRollup } from '../../components/AgreementRollup';

interface SearchResult {
  customers: Customer[];
  total: number;
}

/**
 * The table's columns as data, so the detail row's colSpan is derived from the
 * same list that renders the header rather than a hand-maintained number.
 */
export const CUSTOMER_COLUMNS: { key: string; label: string }[] = [
  { key: 'customer', label: 'Customer' },
  { key: 'account', label: 'Account' },
  { key: 'location', label: 'Location' },
  // Size + CSM added and Industry moved into the panel, 2026-08-11 (Stacie):
  // "see size of client (Enterprise, small, etc); CSM; (can move industry to the
  // dropdown area)". No API change — /api/customers already returned `size` and
  // `cust_csm`, and the panel already rendered all three, so this is purely which
  // of them earns a scannable column.
  { key: 'size', label: 'Size' },
  { key: 'ae', label: 'AE' },
  { key: 'csm', label: 'CSM' },
  { key: 'supplier_mgmt', label: 'Supplier Mgmt' },
];

const TOTAL_COLUMNS = totalColumns(CUSTOMER_COLUMNS.length);

export default function SupplierCustomersPage() {
  const [q, setQ] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // See ExpandableRow for why expansion is component state, and why the stripe
  // parity has to be data-derived once a row can insert a second <tr>.
  const expanded = useExpandedRows();
  const { collapseAll } = expanded;

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(() => {
      setLoading(true);
      setError(null);
      const url = q.trim()
        ? `/api/customers?q=${encodeURIComponent(q.trim())}`
        : '/api/customers';
      apiFetch<SearchResult>(url)
        .then((d) => {
          if (cancelled) return;
          setCustomers(d.customers);
          setTotal(d.total);
          // A new search replaces the rows; a stale id left expanded would open
          // whichever unrelated customer happened to reuse it.
          collapseAll();
        })
        .catch((e) => {
          if (!cancelled)
            setError(e instanceof ApiError ? e.message : String(e));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [q, collapseAll]);

  // Agreement rollups for the listed customers — a SECOND request, keyed by the
  // ids the first one returned.
  //
  // Not folded into /api/customers on purpose: that route is CRM-owned and
  // shared (the CRM list page calls it with ?limit=5000), so an agreement rollup
  // there would be built for five thousand customers to serve a page that never
  // shows it. It is also the capability boundary — /api/customers is gated on
  // view:customers, this on view:supplier_tab. See the API route's docstring.
  //
  // A failure here is deliberately NOT surfaced as a page error: the customer
  // list is still perfectly usable without the rollup, and turning a degraded
  // panel section into a red banner over a working table would be a worse
  // outcome. The section renders its empty state instead.
  const [rollups, setRollups] = useState<Record<string, AgreementRollup>>({});

  const rollupKey = customers.map((c) => c.customer_id).join(',');
  useEffect(() => {
    if (!rollupKey) {
      setRollups({});
      return;
    }
    let cancelled = false;
    apiFetch<{ rollups: Record<string, AgreementRollup> }>(
      `/api/btp/customer-agreements?customer_ids=${encodeURIComponent(rollupKey)}`,
    )
      .then((d) => {
        if (!cancelled) setRollups(d.rollups ?? {});
      })
      .catch(() => {
        // Swallowed by design — see above. Clearing keeps a stale rollup from
        // being shown against a new result set.
        if (!cancelled) setRollups({});
      });
    return () => {
      cancelled = true;
    };
  }, [rollupKey]);

  const enrolled = customers.filter((c) => c.btp_enabled_hint).length;

  return (
    <div className="page" data-testid="supplier-customers-page">
      <div className="page-header">
        <h1>Customers</h1>
        <p className="page-intro">
          Start here: find a customer, then build out their supplier strategy —
          hotels, agreements, and the rate access codes those agreements load
          against. Every customer is listed, whether or not they&rsquo;re
          enrolled in the Supplier Management program.
        </p>
      </div>

      <div className="kpi-row kpi-row-3">
        <div className="kpi-card kpi-highlight">
          <span className="kpi-label">Customers</span>
          <span className="kpi-value">{total.toLocaleString()}</span>
          <span className="kpi-sub">in this view</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Enrolled</span>
          <span className="kpi-value">{enrolled.toLocaleString()}</span>
          <span className="kpi-sub">in Supplier Management</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Shown</span>
          <span className="kpi-value">{customers.length.toLocaleString()}</span>
          <span className="kpi-sub">matching rows</span>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <span className="section-label">Customer list</span>
          <span className="section-meta">
            {loading ? 'Refreshing…' : `${customers.length} shown`}
          </span>
        </div>
        <div className="section-body">
          <div style={{ marginBottom: 14, maxWidth: 380 }}>
            <label className="field-label" htmlFor="supplier-customer-search">
              Search
            </label>
            <input
              className="input"
              data-testid="supplier-customers-search"
              id="supplier-customer-search"
              onChange={(e) => setQ(e.target.value)}
              placeholder="Customer name or account number"
              value={q}
            />
          </div>

          {error && <div className="banner banner-error">{error}</div>}

          <TableScroll>
            <table
              className="data-table"
              data-testid="supplier-customers-table"
            >
              <thead>
                <tr>
                  <DisclosureHeaderCell />
                  {CUSTOMER_COLUMNS.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr className="empty">
                    <td colSpan={TOTAL_COLUMNS}>Loading…</td>
                  </tr>
                )}
                {!loading && customers.length === 0 && (
                  <tr className="empty">
                    <td colSpan={TOTAL_COLUMNS}>
                      No customers match that search.
                    </td>
                  </tr>
                )}
                {customers.flatMap((c, i) => {
                  const isOpen = expanded.isExpanded(c.customer_id);
                  const detailId = detailRowId('customer', c.customer_id);
                  const zebra = zebraFor(i);

                  const rows = [
                    <tr data-zebra={zebra} key={c.customer_id}>
                      <DisclosureCell
                        controls={detailId}
                        label={c.name}
                        onToggle={() => expanded.toggle(c.customer_id)}
                        open={isOpen}
                        testId={`supplier-customers-expand-${c.customer_id}`}
                      />
                      <td style={{ fontWeight: 600 }}>
                        <Link
                          className="row-link"
                          to={`/crm/customers/${encodeURIComponent(c.customer_id)}`}
                        >
                          {c.name}
                        </Link>
                      </td>
                      <td style={{ fontFamily: 'SF Mono, Menlo, monospace' }}>
                        {c.account_number || '—'}
                      </td>
                      <td>
                        {[c.city, c.state].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td>{c.size || '—'}</td>
                      <td>{c.cust_ae || '—'}</td>
                      <td>{c.cust_csm || '—'}</td>
                      <td>
                        {c.btp_enabled_hint ? (
                          <span className="badge badge-green">Enrolled</span>
                        ) : (
                          <span className="badge">Not enrolled</span>
                        )}
                      </td>
                    </tr>,
                  ];

                  if (isOpen) {
                    rows.push(
                      <DetailRow
                        colSpan={TOTAL_COLUMNS}
                        id={detailId}
                        key={`${c.customer_id}-detail`}
                        testId={`supplier-customers-detail-${c.customer_id}`}
                        zebra={zebra}
                      >
                        <CustomerDetail
                          agreements={rollups[c.customer_id]}
                          customer={c}
                        />
                      </DetailRow>,
                    );
                  }
                  return rows;
                })}
              </tbody>
            </table>
          </TableScroll>
        </div>
      </div>
    </div>
  );
}
